import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { usePath } from 'lib/core/hooks/usePath'
import {
  getIsFailure as getCheckTokenFailure,
  getIsFetching as getCheckTokenFetching,
  getIsSuccess as getCheckTokenSuccess
} from 'lib/core/data/customer/checkResetPasswordToken/selectors'
import { getSlice } from 'lib/core/data/customer/resetPassword/selectors'
import { usePrevious } from 'lib/core/hooks/usePrevious'
import { resetPassword as _resetPassword } from 'lib/core/data/customer/resetPassword'
import { useEffect } from 'react'
import { checkResetPasswordToken } from 'lib/core/data/customer/checkResetPasswordToken'
import { Toast } from 'lib/ui'

export const useResetPassword = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation<{ token?: string }>()
  const path = usePath()
  const token = location.state?.token
  const isCheckTokenFetching = useSelector(getCheckTokenFetching)
  const isCheckTokenSuccess = useSelector(getCheckTokenSuccess)
  const isCheckTokenFailure = useSelector(getCheckTokenFailure)
  const {
    isFetching: isResetPasswordFetching,
    isSuccess: isResetPasswordSuccess,
    isFailure: isResetPasswordFailure
  } = useSelector(getSlice)

  const prevIsResetPasswordFetching = usePrevious(isResetPasswordFetching)

  const resetPassword = (values: { password: string }) => {
    const token = location.state?.token
    const data = { ...values }

    dispatch(_resetPassword({
      resetToken: token as string,
      newPassword: data.password
    }))
  }

  const getStatus = (): 'expired' | 'success' | 'idle' | 'loading' => {
    if (isCheckTokenFailure && !isCheckTokenFetching) {
      return 'expired'
    }

    if (isResetPasswordSuccess && !isResetPasswordFetching) {
      return 'success'
    }

    if (isCheckTokenSuccess) {
      return 'idle'
    }

    return 'loading'
  }

  useEffect(() => {
    if (token) {
      dispatch(checkResetPasswordToken({ token }))
    } else {
      history.replace(path.login)
    }
  }, [
    path.login,
    dispatch,
    history,
    token
  ])

  useEffect(() => { // TODO: add callback to resetPassword action, remove this useEffect.
    const hasFailed = !isResetPasswordFetching && isResetPasswordFailure && prevIsResetPasswordFetching
    if (hasFailed) {
      Toast.toastFailure({ messageId: 'app.whoops' })
    }
  }, [
    isResetPasswordFetching,
    isResetPasswordFailure,
    prevIsResetPasswordFetching
  ])

  return {
    status: getStatus(),
    isFetching: isResetPasswordFetching,
    resetPassword
  }
}
